import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useAsyncValue } from 'framework/utils';
import { PoliciesDataType, useComplianceReportService } from 'domain/shared/compliance';
import EmployeeComplianceDocumentNotification from './EmployeeComplianceDocumentNotification';
import { useAuthorization } from 'framework/authorization';
import { NotificationItemProps } from 'infrastructure/notification/types';
import { NOTIFICATIONS_TYPES } from 'infrastructure/notification/constants';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { EMPLOYEE_COMPLIANCE_READ } from 'domain/shared/employee';
import {
  isComplianceRegistryItemNotSigned,
  isRegistryItemNew,
  sortComplianceRegistryItemsForPortal,
} from '../complianceRegistryPortalRules';

const useEmployeeComplianceDocumentNotifications = (): NotificationItemProps<PoliciesDataType[]>[] => {
  const { isGranted } = useAuthorization();
  const { isFeatureEnabled } = useFeatureToggle();
  const { getUserAlias } = useAuthenticatedUserService();
  const { getMyCompliancies } = useComplianceReportService();
  const currentUserAlias = useAsyncValue(async () => getUserAlias());

  const isItemApplicableForNotification = (item: PoliciesDataType) =>
    isComplianceRegistryItemNotSigned(item) || isRegistryItemNew(item);

  const searchEmployeeComplianceDocuments = async () => {
    const policies = await getMyCompliancies();

    return sortComplianceRegistryItemsForPortal(policies.filter(item => isItemApplicableForNotification(item)));
  };

  const isShowNotification = isGranted(EMPLOYEE_COMPLIANCE_READ);
  const isEmployeePortalComplianceEnabled = isFeatureEnabled(Features.COMPLIANCE_NOTIFICATIONS);

  if (!currentUserAlias || !isShowNotification || !isEmployeePortalComplianceEnabled) return [];

  return [
    {
      type: NOTIFICATIONS_TYPES.POLICY,
      autoUpdate: true,
      loadData: () => searchEmployeeComplianceDocuments(),
      isVisible: (response: PoliciesDataType[]) => {
        return response?.length > 0 && response?.some(item => isItemApplicableForNotification(item));
      },
      getCount: (response: PoliciesDataType[]) => response?.length ?? 0,
      component: (response: PoliciesDataType[]) => <EmployeeComplianceDocumentNotification response={response} />,
    },
  ];
};

export default useEmployeeComplianceDocumentNotifications;
